/**
 * R4-C: read-only research review inside the existing Control Center.
 *
 * This is not the approval queue. A job can be `succeeded` while individual evidence
 * records are still `needs_review`; those are different statuses and the UI shows both.
 * There is deliberately no approve/promote/send control here.
 */

import { useEffect, useState } from "react";
import { ResearchBrowser } from "./ResearchBrowser";

import {
  api,
  ApiError,
  formatMicros,
  Job,
  JobDetail,
  ResearchResult,
  ResearchResultAvailable,
} from "../api/client";
import {
  classificationLabel,
  classifyOutcomes,
  filterOutcomes,
  parseResearchResult,
  reasonLabel,
  ResearchFilter,
  ResearchOutcome,
  safeHttpUrl,
  summaryMatches,
  unavailableReasonLabel,
} from "./researchResult";

export interface ResearchJobPage {
  items: Job[];
  next_cursor?: string | null;
}

export interface ResearchLoader {
  listJobs(
    projectId: string,
    options?: { cursor?: string | null },
  ): Promise<ResearchJobPage>;
  getJob(projectId: string, jobId: string): Promise<JobDetail>;
}

const JOB_STATUS_HR: Record<string, string> = {
  queued: "u redu",
  running: "izvršava se",
  awaiting_approval: "čeka odobrenje",
  awaiting_budget_approval: "čeka budžet",
  succeeded: "uspješno",
  failed: "neuspješno",
  cancelled: "otkazano",
  killed: "zaustavljeno",
  needs_review: "za pregled",
};

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.messageHr : String(error);
}

export function ResearchPanel({ projectId, loader = api }: {
  projectId: string;
  loader?: ResearchLoader;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [job, setJob] = useState<JobDetail | null>(null);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setJob(null);
    setEmpty(false);
    void (async () => {
      try {
        // `/jobs` is ordered by the random UUID `_id`, not by `created_at`, and is
        // paginated. Collect every page before choosing a research job: sorting one
        // page can hide a research job (or a newer successful result) on a later page.
        const researchJobs: Job[] = [];
        const usedCursors = new Set<string>();
        let cursor: string | null = null;
        for (;;) {
          const listing = await loader.listJobs(projectId, { cursor });
          if (!active) return;
          researchJobs.push(
            ...listing.items.filter((row) => row.workflow === "g4.lead_research"),
          );
          const next = listing.next_cursor ?? null;
          if (!next) break;
          // A cursor that repeats would loop forever and, worse, could leave a partial
          // page set indistinguishable from a complete one. Fail closed instead.
          if (usedCursors.has(next)) throw new Error("job_pagination_cursor_repeat");
          usedCursors.add(next);
          cursor = next;
        }
        researchJobs.sort((left, right) =>
          String(right.created_at).localeCompare(String(left.created_at)),
        );
        // Prefer the newest completed research run; a later failed/revoked run must not
        // hide the last valid result. If none succeeded, show the newest run's explicit
        // unavailable state.
        const candidate = researchJobs.find((row) => row.status === "succeeded")
          ?? researchJobs[0];
        if (!candidate) {
          setEmpty(true);
          return;
        }
        const detail = await loader.getJob(projectId, candidate.id);
        if (active) setJob(detail);
      } catch (err) {
        if (active) setError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [projectId, loader]);

  return (
    <div className="space-y-4" data-testid="research-panel">
      <div>
        <h2 className="text-lg font-semibold">Istraživački rezultati</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Odvojeno od reda odobrenja. Prikazuje samo provjereni R4 rezultat i nikad ne
          mijenja status zapisa ni posla.
        </p>
      </div>
      {error != null && (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
           role="alert" data-testid="research-error">
          {errorText(error)}
        </p>
      )}
      {loading && (
        <p className="text-sm text-muted-foreground" data-testid="research-loading">
          Učitavanje istraživanja…
        </p>
      )}
      {!loading && empty && (
        <p className="rounded border border-border p-4 text-sm text-muted-foreground"
           data-testid="research-empty">
          U ovom projektu nema R4 istraživačkog posla.
        </p>
      )}
      {/* `project_id` is checked as well as `loading`: on a project switch the effect
          runs after the render, so this prevents a single frame of the old project's
          result from ever being shown. */}
      {!loading && job && job.project_id === projectId && (
        <ResearchResultView
          key={job.id}
          result={job.result as ResearchResult | null | undefined}
          jobStatus={job.status}
          jobId={job.id}
        />
      )}
    </div>
  );
}

export function ResearchResultView({
  result,
  jobStatus,
  jobId,
}: {
  result: ResearchResult | null | undefined;
  jobStatus: string;
  jobId: string;
}) {
  const [filter, setFilter] = useState<ResearchFilter>("all");
  const status = JOB_STATUS_HR[jobStatus] ?? jobStatus;
  // API data is `unknown` until this runtime gate accepts it. A version/state or
  // nested shape this build does not know must never reach map/arithmetic.
  const parsed = parseResearchResult(result);

  if (parsed === null) {
    const reason = result == null ? undefined : "invalid_projection";
    return (
      <UnavailableView
        jobStatus={status}
        jobId={jobId}
        reason={reason}
        generic={result == null}
      />
    );
  }

  if (parsed.state === "unavailable") {
    return (
      <UnavailableView
        jobStatus={status}
        jobId={jobId}
        reason={parsed.reason}
      />
    );
  }

  if (!summaryMatches(parsed)) {
    return (
      <section className="rounded border border-destructive/40 p-4"
               data-testid="research-arithmetic-error">
        <p className="text-sm" data-testid="research-job-status">
          Status obrade posla: <strong>{status}</strong>{" "}
          <span className="font-mono text-xs text-muted-foreground">{jobId}</span>
        </p>
        <p className="mt-2 text-sm text-destructive">
          Sažetak se ne slaže sa zapisima. Prikaz je blokiran umjesto da se brojevi
          poprave u pregledniku.
        </p>
      </section>
    );
  }

  return (
    <ResearchAvailableView
      result={parsed}
      jobStatus={status}
      jobId={jobId}
      filter={filter}
      onFilter={setFilter}
    />
  );
}

function UnavailableView({
  jobStatus,
  jobId,
  reason,
  generic = false,
}: {
  jobStatus: string;
  jobId: string;
  reason?: string;
  generic?: boolean;
}) {
  return (
    <section className="rounded border border-border p-4"
             data-testid="research-unavailable"
             data-reason={reason}>
      <p className="text-sm" data-testid="research-job-status">
        Status obrade posla: <strong>{jobStatus}</strong>{" "}
        <span className="font-mono text-xs text-muted-foreground">{jobId}</span>
      </p>
      {generic || !reason ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Ova projekcija rezultata nije dostupna. Status posla nije status zapisa.
        </p>
      ) : (
        <p className="mt-2 text-sm text-destructive" data-testid="research-unavailable-reason">
          {unavailableReasonLabel(reason)}
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Ne prikazuju se djelomični podaci, izvori ni trošak.
      </p>
    </section>
  );
}

function ResearchAvailableView({
  result,
  jobStatus,
  jobId,
  filter,
  onFilter,
}: {
  result: ResearchResultAvailable;
  jobStatus: string;
  jobId: string;
  filter: ResearchFilter;
  onFilter: (filter: ResearchFilter) => void;
}) {
  const outcomes = classifyOutcomes(result);
  const visible = filterOutcomes(outcomes, filter);
  // Only authorized, fully measured zero actual cost may make the measured-zero
  // claim. Missing/withheld cost, unknown cost, a nonzero amount or any unavailable
  // item must never be presented as a measured zero.
  const measuredZeroCost =
    result.cost !== undefined &&
    result.cost.known &&
    result.cost.actual_micros === 0 &&
    result.cost.unavailable_count === 0;
  const filters: { id: ResearchFilter; label: string; count: number }[] = [
    { id: "all", label: "Svi", count: outcomes.length },
    { id: "accept", label: "Prihvaćeni", count: result.summary.accepted },
    { id: "needs_review", label: "Za pregled", count: result.summary.needs_review },
    { id: "reject", label: "Odbijeni", count: result.summary.rejected },
  ];

  return (
    <section className="space-y-4" data-testid="research-result">
      <div className="rounded border border-border p-4">
        <p className="text-sm" data-testid="research-job-status">
          Status obrade posla: <strong>{jobStatus}</strong>{" "}
          <span className="font-mono text-xs text-muted-foreground">{jobId}</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Posao može biti uspješan dok pojedini zapisi još čekaju pregled dokaza. To
          nije odobrenje i ovdje se ne može pretvoriti u prihvaćanje.
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="Proizvedeno" value={result.summary.produced}
                  testId="research-summary-produced" />
          <Metric label="Prihvaćeno" value={result.summary.accepted}
                  testId="research-summary-accepted" />
          <Metric label="Za pregled" value={result.summary.needs_review}
                  testId="research-summary-needs-review" />
          <Metric label="Odbijeno" value={result.summary.rejected}
                  testId="research-summary-rejected" />
        </dl>
        <p className="mt-3 text-xs text-muted-foreground">
          Fixture: {result.fixture.id} v{result.fixture.version} · referentni datum{" "}
          {result.evaluated_on} · snapshot <span className="break-all font-mono">
            {result.snapshot_hash}
          </span>
        </p>
      </div>

      <div className="rounded border border-border p-4" data-testid="research-cost">
        <h3 className="text-sm font-semibold">Trošak ovog posla</h3>
        {result.cost === undefined ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Trošak nije dostupan bez ovlasti <code>budget.view</code>.
          </p>
        ) : !result.cost.known ? (
          <p className="mt-1 text-sm text-warn" data-testid="research-cost-unknown">
            Trošak nije poznat. Nedostajući iznos se ne prikazuje kao nula.
          </p>
        ) : (
          <dl className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">Stvarni trošak</dt>
              <dd data-testid="research-cost-actual">
                {formatMicros(result.cost.actual_micros)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Procijenjeni</dt>
              <dd>{formatMicros(result.cost.estimated_micros)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Nepoznatih stavki</dt>
              <dd>{result.cost.unavailable_count ?? 0}</dd>
            </div>
          </dl>
        )}
        {measuredZeroCost && (
          <p className="mt-2 text-xs text-muted-foreground"
             data-testid="research-cost-measured-zero">
            Sintetički R4 mock put je stvarno izmjeren na nuli i ne stvara plaćene
            rezervacije.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="research-filters">
        {filters.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => onFilter(entry.id)}
            aria-pressed={filter === entry.id}
            className={`rounded px-3 py-1.5 text-sm ${
              filter === entry.id
                ? "bg-primary text-primary-foreground"
                : "border border-border"
            }`}
            data-testid={`research-filter-${entry.id}`}
          >
            {entry.label} ({entry.count})
          </button>
        ))}
      </div>

      <ResearchBrowser
        filterKey={filter}
        outcomes={outcomes}
        filteredOutcomes={visible}
        renderDetail={(outcome, index) => <OutcomeRow outcome={outcome} index={index} />}
        onResetStatus={() => onFilter("all")}
      />
    </section>
  );
}

function Metric({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div className="rounded border border-border p-3" data-testid={testId}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold">{value}</dd>
    </div>
  );
}

function OutcomeRow({ outcome, index }: { outcome: ResearchOutcome; index: number }) {
  if (outcome.kind === "contract_rejection") {
    return (
      <li className="rounded border border-border p-4"
          data-testid={`research-outcome-${index}`}
          data-classification="reject">
        <p className="font-mono text-sm">{outcome.rejection.organization_id}</p>
        <p className="mt-1 text-sm font-semibold">Ugovorno odbijeno</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Kod: <span className="font-mono">{outcome.rejection.code}</span>
        </p>
        {outcome.rejection.detail && (
          <p className="mt-1 text-xs break-words">{outcome.rejection.detail}</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          Nema fit/confidence ocjene jer zapis nije prošao ugovor.
        </p>
      </li>
    );
  }

  const evaluation = outcome.evaluation;
  const name = evaluation.organization.name ?? evaluation.organization_id;
  return (
    <li className="rounded border border-border p-4"
        data-testid={`research-outcome-${index}`}
        data-classification={evaluation.classification}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{name}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {evaluation.organization_id}
          </p>
        </div>
        <span className="rounded border border-border px-2 py-1 text-xs">
          {classificationLabel(evaluation.classification)}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">Fit</dt>
          <dd data-testid={`research-fit-${index}`}>{evaluation.fit_score}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Pouzdanost</dt>
          <dd data-testid={`research-confidence-${index}`}>{evaluation.confidence}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Tip</dt>
          <dd>{evaluation.organization.type ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Država / grad</dt>
          <dd>
            {[evaluation.organization.country, evaluation.organization.city]
              .filter(Boolean)
              .join(" / ") || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Članstvo</dt>
          <dd>{evaluation.organization.member_estimate_band ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Web</dt>
          <dd className="break-all">
            {evaluation.organization.website ?? "—"}
          </dd>
        </div>
      </dl>

      <p className="mt-3 text-xs text-muted-foreground">
        Razlozi: {evaluation.reason_codes.map(reasonLabel).join(", ")}
      </p>
      {evaluation.duplicate_of && (
        <p className="mt-1 text-xs text-muted-foreground">
          Duplikat od: <span className="font-mono">{evaluation.duplicate_of}</span>
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Nedostaje: {evaluation.missing_fields.length > 0
          ? evaluation.missing_fields.join(", ")
          : "ništa"}
      </p>

      <div className="mt-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Izvori</p>
        <ul className="mt-1 space-y-2">
          {evaluation.sources.map((source, sourceIndex) => {
            const href = safeHttpUrl(source.url);
            return (
              <li key={`${source.url}-${sourceIndex}`} className="text-xs">
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                     className="break-all underline">
                    {source.url}
                  </a>
                ) : (
                  <span className="text-destructive">[izvor nije prikazan]</span>
                )}
                <span className="ml-2 text-muted-foreground">{source.observed_at}</span>
                <p className="mt-0.5 break-words">{source.claim}</p>
              </li>
            );
          })}
          {evaluation.sources.length === 0 && (
            <li className="text-xs text-muted-foreground">Nema izvora.</li>
          )}
        </ul>
      </div>
    </li>
  );
}
